import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Upload, AlertTriangle, Navigation, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

const EnhancedFailedDeliveryModal = ({ order, onClose, onSubmit }) => {
    const [failureReason, setFailureReason] = useState('');
    const [failureNotes, setFailureNotes] = useState('');
    const [isBadlyRouted, setIsBadlyRouted] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [showAddressCorrection, setShowAddressCorrection] = useState(false);
    const [correctedAddress, setCorrectedAddress] = useState('');
    const [correctedLatitude, setCorrectedLatitude] = useState('');
    const [correctedLongitude, setCorrectedLongitude] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [failureLatitude, setFailureLatitude] = useState(null);
    const [failureLongitude, setFailureLongitude] = useState(null);
    const [gpsLoading, setGpsLoading] = useState(true);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    // Auto-capturar GPS al abrir el modal
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFailureLatitude(position.coords.latitude);
                    setFailureLongitude(position.coords.longitude);
                    setGpsLoading(false);
                },
                () => {
                    setGpsLoading(false);
                    toast.error('No se pudo obtener GPS. Activa tu ubicación.');
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        } else {
            setGpsLoading(false);
        }
    }, []);

    const failureReasons = [
        { value: 'cliente_ausente', label: 'Cliente no estaba / no contestó' },
        { value: 'cliente_rechazo', label: 'Cliente rechazó el pedido' },
        { value: 'direccion_incorrecta', label: 'Dirección incorrecta' },
        { value: 'problemas_trafico', label: 'Problemas de tráfico/tiempo' },
        { value: 'otro', label: 'Otro (especificar en notas)' }
    ];

    const handlePhotoCapture = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (cameraInputRef.current) cameraInputRef.current.value = '';
    };

    const handleGetCurrentLocation = () => {
        if (navigator.geolocation) {
            toast.loading('Obteniendo ubicación...');
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    toast.dismiss();
                    setCorrectedLatitude(position.coords.latitude.toFixed(8));
                    setCorrectedLongitude(position.coords.longitude.toFixed(8));
                    toast.success('Ubicación obtenida');
                },
                () => {
                    toast.dismiss();
                    toast.error('No se pudo obtener la ubicación');
                }
            );
        } else {
            toast.error('Geolocalización no disponible');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones
        if (!failureReason) {
            toast.error('Debes seleccionar una razón de fallo');
            return;
        }

        if (failureReason === 'otro' && !failureNotes.trim()) {
            toast.error('Debes especificar la razón en las notas');
            return;
        }

        if (showAddressCorrection && !correctedAddress.trim()) {
            toast.error('Debes ingresar la dirección corregida');
            return;
        }

        // Foto obligatoria
        if (!photo) {
            toast.error('Debes tomar una foto de evidencia para registrar la entrega como fallida');
            return;
        }

        // GPS obligatorio
        if (!failureLatitude) {
            toast.error('Se requiere ubicación GPS. Activa tu GPS y espera unos segundos.');
            return;
        }

        setSubmitting(true);
        try {
            const data = {
                failureReason,
                failureNotes: failureNotes.trim() || null,
                isBadlyRouted,
                failurePhoto: photo,
                latitude: failureLatitude,
                longitude: failureLongitude
            };

            // Si hay corrección de dirección, enviar esa acción también
            let addressCorrectionData = null;
            if (showAddressCorrection) {
                addressCorrectionData = {
                    correctedAddress,
                    correctedLatitude: correctedLatitude ? parseFloat(correctedLatitude) : null,
                    correctedLongitude: correctedLongitude ? parseFloat(correctedLongitude) : null
                };
            }

            await onSubmit(data, addressCorrectionData);
            onClose();
        } catch (error) {
            console.error('Error submitting failed delivery:', error);
            toast.error(error.response?.data?.message || 'Error al registrar entrega fallida');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-red-600 text-white p-4 flex justify-between items-center rounded-t-lg">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        <h2 className="text-lg font-bold">Marcar Como Fallida</h2>
                    </div>
                    <button onClick={onClose} className="text-white hover:text-red-100">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Order Info */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="font-semibold text-gray-900">Pedido: {order.order_number || order.orderNumber}</p>
                        <p className="text-sm text-gray-600">{order.customer_name || order.customerName}</p>
                    </div>

                    {/* Failure Reason */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Razón del fallo <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                            {failureReasons.map((reason) => (
                                <label
                                    key={reason.value}
                                    className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${failureReason === reason.value
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="failureReason"
                                        value={reason.value}
                                        checked={failureReason === reason.value}
                                        onChange={(e) => {
                                            setFailureReason(e.target.value);
                                            // Mostrar form de corrección si selecciona "dirección incorrecta"
                                            setShowAddressCorrection(e.target.value === 'direccion_incorrecta');
                                        }}
                                        className="mr-3"
                                    />
                                    <span className="text-sm font-medium text-gray-700">{reason.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Address Correction (conditional) */}
                    {showAddressCorrection && (
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-2 text-blue-700 font-semibold">
                                <MapPin className="w-5 h-5" />
                                <span>Corrección de Dirección</span>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Dirección Correcta <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={correctedAddress}
                                    onChange={(e) => setCorrectedAddress(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    rows="2"
                                    placeholder="Ej: Calle 123 #45-67, Apto 301"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitud</label>
                                    <input
                                        type="text"
                                        value={correctedLatitude}
                                        onChange={(e) => setCorrectedLatitude(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="4.1234567"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitud</label>
                                    <input
                                        type="text"
                                        value={correctedLongitude}
                                        onChange={(e) => setCorrectedLongitude(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="-74.1234567"
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleGetCurrentLocation}
                                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <Navigation className="w-4 h-4" />
                                Usar Mi Ubicación Actual
                            </button>

                            <p className="text-xs text-gray-600">
                                💡 Ingresa la dirección correcta y opcionalmente las coordenadas para futura referencia.
                            </p>
                        </div>
                    )}

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notas adicionales {failureReason === 'otro' && <span className="text-red-500">*</span>}
                        </label>
                        <textarea
                            value={failureNotes}
                            onChange={(e) => setFailureNotes(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            rows="3"
                            placeholder="Describe cualquier detalle adicional relevante..."
                        />
                    </div>

                    {/* GPS Status */}
                    <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${failureLatitude ? 'bg-green-50 text-green-700 border border-green-200' : gpsLoading ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                        <Navigation className="w-4 h-4" />
                        {gpsLoading ? 'Obteniendo ubicación GPS...' : failureLatitude ? `📍 GPS capturado: ${failureLatitude.toFixed(6)}, ${failureLongitude.toFixed(6)}` : '⚠️ GPS no disponible. Activa tu ubicación.'}
                    </div>

                    {/* Photo Evidence */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Foto de evidencia <span className="text-red-500">*</span>
                        </label>

                        {!photoPreview ? (
                            <div className="space-y-2">
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handlePhotoCapture}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => cameraInputRef.current?.click()}
                                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Camera className="w-5 h-5" />
                                    Tomar Foto
                                </button>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoCapture}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Upload className="w-5 h-5" />
                                    Subir Desde Galería
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <img
                                    src={photoPreview}
                                    alt="Preview"
                                    className="w-full rounded-lg border-2 border-gray-300"
                                />
                                <button
                                    type="button"
                                    onClick={handleRemovePhoto}
                                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Badly Routed Flag */}
                    <label className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isBadlyRouted}
                            onChange={(e) => setIsBadlyRouted(e.target.checked)}
                            className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                        />
                        <div>
                            <p className="font-medium text-gray-900">Esta entrega fue mal enrutada</p>
                            <p className="text-xs text-gray-600">
                                Marca esto si el fallo se debió a mala planificación de la ruta
                            </p>
                        </div>
                    </label>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                            disabled={submitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={submitting || !failureReason || !photo || !failureLatitude}
                        >
                            {submitting ? 'Registrando...' : 'Marcar Como Fallida'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnhancedFailedDeliveryModal;
